import React from 'react';
import { Capacitor } from '@capacitor/core';

const ImageOverlay = ({ isOpen, imageUrl, imageAlt, onClose, onNext, onPrev, hasNext, hasPrev, type, youtubeId }) => {
  const [isZoomed, setIsZoomed] = React.useState(false);
  const [imageDimensions, setImageDimensions] = React.useState({ width: 0, height: 0 });
  const [isPlayingVideo, setIsPlayingVideo] = React.useState(false);
  const imageRef = React.useRef(null);
  const isYouTube = type === 'youtube' && youtubeId;

  // YouTube refuses to play when it can't verify the embedding page's origin, and
  // the native app is served from capacitor://localhost, which it rejects. Route
  // the native player through a page on our own https domain, which YouTube
  // accepts; the web build embeds directly as before.
  // Bump YOUTUBE_PROXY_VERSION whenever the hosted proxy page changes. WKWebView
  // caches cross-origin iframe documents aggressively; before the proxy existed on
  // hosting, this URL returned the SPA shell and that wrong response stuck around
  // even after deploy until the cache key changed.
  const YOUTUBE_PROXY_VERSION = '2';
  const youtubeEmbedSrc = Capacitor.isNativePlatform()
    ? `https://pedalxchange.com/youtube-embed.html?v=${encodeURIComponent(youtubeId || '')}&pv=${YOUTUBE_PROXY_VERSION}`
    : `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`;

  React.useEffect(() => {
    setIsZoomed(false);
    setIsPlayingVideo(false);
  }, [isOpen, imageUrl]);

  // Detect when user pinch-zooms out on mobile and reset to non-zoomed state
  React.useEffect(() => {
    if (!isOpen || !isZoomed || !isTouchDevice()) return;

    let initialPinchDistance = null;
    let isPinching = false;

    const getTouchDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        isPinching = true;
        initialPinchDistance = getTouchDistance(e.touches);
      }
    };

    const handleTouchMove = (e) => {
      if (isPinching && e.touches.length === 2 && initialPinchDistance) {
        const currentDistance = getTouchDistance(e.touches);
        const scale = currentDistance / initialPinchDistance;
        
        // If user has pinched to less than 50% of initial distance, exit zoom
        if (scale < 0.5) {
          setIsZoomed(false);
          isPinching = false;
          initialPinchDistance = null;
        }
      }
    };

    const handleTouchEnd = (e) => {
      if (e.touches.length < 2) {
        isPinching = false;
        initialPinchDistance = null;
      }
    };

    const img = imageRef.current;
    if (img) {
      img.addEventListener('touchstart', handleTouchStart, { passive: true });
      img.addEventListener('touchmove', handleTouchMove, { passive: true });
      img.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    return () => {
      if (img) {
        img.removeEventListener('touchstart', handleTouchStart);
        img.removeEventListener('touchmove', handleTouchMove);
        img.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [isOpen, isZoomed]);

  // Load image to get natural dimensions
  React.useEffect(() => {
    if (isOpen && imageUrl) {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.src = imageUrl;
    }
  }, [isOpen, imageUrl]);

  // Add event listener for escape key, arrow keys, and manage body scroll
  React.useEffect(() => {
    const handleKeyDownEvent = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight' && hasNext && onNext) {
        onNext();
      } else if (e.key === 'ArrowLeft' && hasPrev && onPrev) {
        onPrev();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDownEvent);
      // Prevent body scroll when overlay is open
      document.body.style.overflow = 'hidden';
      // Only prevent page zoom when NOT zoomed - allow it when zoomed for pinch-zoom.
      // NATIVE: never touch the viewport. Toggling user-scalable on WKWebView is
      // unreliable and can leave pinch-zoom permanently disabled app-wide. On
      // native we leave the (permissive) viewport alone so pinch-zoom always works.
      const viewport = Capacitor.isNativePlatform() ? null : document.querySelector('meta[name="viewport"]');
      if (viewport && isTouchDevice() && !isZoomed) {
        viewport.setAttribute('data-original-content', viewport.getAttribute('content'));
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
      } else if (viewport && isTouchDevice() && isZoomed) {
        // When zoomed, allow pinch-zoom
        if (viewport.hasAttribute('data-original-content')) {
          viewport.setAttribute('content', viewport.getAttribute('data-original-content'));
          viewport.removeAttribute('data-original-content');
        }
      }
    } else {
      document.body.style.overflow = 'unset';
      // Restore viewport settings (web only; native never altered it)
      const viewport = Capacitor.isNativePlatform() ? null : document.querySelector('meta[name="viewport"]');
      if (viewport && viewport.hasAttribute('data-original-content')) {
        viewport.setAttribute('content', viewport.getAttribute('data-original-content'));
        viewport.removeAttribute('data-original-content');
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDownEvent);
      document.body.style.overflow = 'unset';
      // Restore viewport settings on cleanup (web only; native never altered it)
      const viewport = Capacitor.isNativePlatform() ? null : document.querySelector('meta[name="viewport"]');
      if (viewport && viewport.hasAttribute('data-original-content')) {
        viewport.setAttribute('content', viewport.getAttribute('data-original-content'));
        viewport.removeAttribute('data-original-content');
      }
    };
  }, [isOpen, isZoomed, onClose, onNext, onPrev, hasNext, hasPrev]);

  // Handle click on overlay background to close
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle image click to toggle zoom
  const handleImageClick = (e) => {
    e.stopPropagation();
    setIsZoomed(!isZoomed);
  };

  // Detect if user is on mobile/touch device
  const isTouchDevice = () => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  };

  if (!isOpen) return null;

  // Check if image has enough resolution to zoom
  const canZoom = imageDimensions.width > 800 || imageDimensions.height > 800;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
        touchAction: 'none'
      }}
      onClick={handleOverlayClick}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          cursor: 'pointer',
          fontSize: '20px',
          fontWeight: 'bold',
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}
        title="Close (Esc)"
      >
        ×
      </button>

      {/* Zoom indicator - only for images */}
      {!isYouTube && canZoom && (
        <div
          style={{
            position: 'absolute',
            top: '70px',
            right: '20px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#333',
            zIndex: 1001
          }}
        >
          {isZoomed ? '🔍 Click to zoom out' : '🔍 Click to zoom'}
        </div>
      )}

      {isYouTube ? (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'relative',
            width: '90vw',
            maxWidth: '900px',
            aspectRatio: '16 / 9',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}
        >
          {isPlayingVideo ? (
            <iframe
              src={youtubeEmbedSrc}
              title={imageAlt}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <div
              onClick={() => setIsPlayingVideo(true)}
              style={{ cursor: 'pointer', width: '100%', height: '100%', position: 'relative' }}
            >
              <img
                src={`https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`}
                alt={imageAlt}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => { e.target.src = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`; }}
              />
              <div style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: '72px', height: '72px', backgroundColor: 'rgba(0,0,0,0.75)', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'transform 0.2s'
              }}>
                <div style={{ width: 0, height: 0, borderTop: '16px solid transparent', borderBottom: '16px solid transparent', borderLeft: '28px solid white', marginLeft: '5px' }} />
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Scrollable Image container */
        <div
          style={{
            position: (isZoomed && isTouchDevice()) ? 'fixed' : 'relative',
            top: (isZoomed && isTouchDevice()) ? '50%' : 'auto',
            left: (isZoomed && isTouchDevice()) ? '50%' : 'auto',
            transform: (isZoomed && isTouchDevice()) ? 'translate(-50%, -50%)' : 'none',
            overflow: isZoomed ? 'auto' : 'visible',
            borderRadius: '8px',
            display: 'flex',
            alignItems: (isZoomed && isTouchDevice()) ? 'center' : (isZoomed ? 'flex-start' : 'center'),
            justifyContent: (isZoomed && isTouchDevice()) ? 'center' : (isZoomed ? 'flex-start' : 'center'),
            touchAction: isZoomed && isTouchDevice() ? 'pan-x pan-y pinch-zoom' : 'none',
            maxWidth: isZoomed ? '90vw' : '95vw',
            maxHeight: isZoomed ? '90vh' : '95vh',
            width: isZoomed ? '90vw' : 'auto',
            height: isZoomed ? '90vh' : 'auto',
            zIndex: (isZoomed && isTouchDevice()) ? 10001 : 'auto'
          }}
          onTouchStart={(e) => {
            if (isZoomed && isTouchDevice()) {
              e.stopPropagation();
            }
          }}
          onTouchMove={(e) => {
            if (isZoomed && isTouchDevice()) {
              e.stopPropagation();
            }
          }}
        >
          <img
            ref={imageRef}
            src={imageUrl}
            alt={imageAlt}
            onClick={canZoom ? handleImageClick : undefined}
            style={{
              display: 'block',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              cursor: canZoom ? (isZoomed ? 'zoom-out' : 'zoom-in') : 'default',
              transition: 'all 0.3s ease',
              width: isZoomed ? (isTouchDevice() ? '175%' : '120%') : 'auto',
              height: 'auto',
              minWidth: isZoomed ? (isTouchDevice() ? '175%' : '120%') : 'auto',
              minHeight: isZoomed ? (isTouchDevice() ? '175%' : '120%') : 'auto',
              maxWidth: isZoomed ? 'none' : '95vw',
              maxHeight: isZoomed ? 'none' : '95vh',
              objectFit: 'contain',
              flexShrink: 0
            }}
          />
        </div>
      )}

      {/* Navigation arrows - only show when not zoomed */}
      {!isZoomed && (hasPrev || hasNext) && (
        <>
          {/* Previous button */}
          {hasPrev && onPrev && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPrev();
              }}
              style={{
                position: 'absolute',
                left: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                cursor: 'pointer',
                fontSize: '32px',
                fontWeight: 'normal',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1001,
                transition: 'background-color 0.2s',
                lineHeight: '0',
                paddingBottom: '0px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
              }}
              title="Previous image (←)"
            >
              <span style={{ marginTop: '-6px' }}>‹</span>
            </button>
          )}

          {/* Next button */}
          {hasNext && onNext && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onNext();
              }}
              style={{
                position: 'absolute',
                right: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                cursor: 'pointer',
                fontSize: '32px',
                fontWeight: 'normal',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1001,
                transition: 'background-color 0.2s',
                lineHeight: '0',
                paddingBottom: '0px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
              }}
              title="Next image (→)"
            >
              <span style={{ marginTop: '-6px' }}>›</span>
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default ImageOverlay;
